'use client';

import { useEffect, useState } from 'react';

import {
  useCreateServiceGroupMutation,
  useUpdateServiceGroupMutation,
} from '@/features/service-types/api/service-typesApi';
import { createServiceGroupSchema } from '@/features/service-types/schemas';
import { MainButton, SecondaryButton, SmartInput } from '@web/ui';
import { getErrorMessage, notify } from '@web/utils';

import styles from './ServiceTypeModalForm.module.scss';

import type { ServiceGroup } from '@web/types';

type ServiceGroupModalFormMode = 'create' | 'edit';

type ServiceGroupModalFormProps = {
  mode: ServiceGroupModalFormMode;
  serviceGroup?: ServiceGroup | null;
  onCancel?: () => void;
  onSuccess?: () => void;
};

type ServiceGroupFormState = {
  name: string;
  description: string;
};

function toFormState(
  serviceGroup?: ServiceGroup | null,
): ServiceGroupFormState {
  if (!serviceGroup) {
    return {
      name: '',
      description: '',
    };
  }

  return {
    name: serviceGroup.name ?? '',
    description: serviceGroup.description ?? '',
  };
}

function isValidState(values: ServiceGroupFormState) {
  return createServiceGroupSchema.shape.name.safeParse(values.name).success;
}

export function ServiceGroupModalForm({
  mode,
  serviceGroup,
  onCancel,
  onSuccess,
}: ServiceGroupModalFormProps) {
  const [values, setValues] = useState<ServiceGroupFormState>(() =>
    toFormState(serviceGroup),
  );
  const [createServiceGroup, { isLoading: isCreating }] =
    useCreateServiceGroupMutation();
  const [updateServiceGroup, { isLoading: isUpdating }] =
    useUpdateServiceGroupMutation();

  useEffect(() => {
    setValues(toFormState(serviceGroup));
  }, [serviceGroup]);

  const isValid = isValidState(values);
  const isLoading = isCreating || isUpdating;
  const submitLabel = mode === 'create' ? 'Create' : 'Save';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) {
      return;
    }

    try {
      if (mode === 'create') {
        await createServiceGroup({
          name: values.name.trim(),
          description: values.description.trim(),
        }).unwrap();
        notify({ type: 'success', message: 'Service group created.' });
      } else if (serviceGroup) {
        await updateServiceGroup({
          serviceGroupId: serviceGroup.id,
          payload: {
            name: values.name.trim(),
            description: values.description.trim(),
          },
        }).unwrap();
        notify({ type: 'success', message: 'Service group updated.' });
      }

      onSuccess?.();
    } catch (error) {
      notify({ type: 'error', message: getErrorMessage(error) });
    }
  };

  return (
    <form className={styles.serviceTypeModalForm} onSubmit={handleSubmit}>
      <SmartInput
        type="text"
        name="service-group-name"
        label="Name"
        value={values.name}
        onChange={(nextValue) =>
          setValues((current) => ({ ...current, name: nextValue }))
        }
        schema={createServiceGroupSchema.shape.name}
      />

      <SmartInput
        type="textarea"
        name="service-group-description"
        label="Description"
        value={values.description}
        onChange={(nextValue) =>
          setValues((current) => ({ ...current, description: nextValue }))
        }
      />

      <div className={styles.serviceTypeModalForm__actions}>
        <SecondaryButton
          ariaLabel="Cancel service group form"
          onClick={onCancel}
        >
          Cancel
        </SecondaryButton>
        <MainButton
          type="submit"
          ariaLabel={submitLabel}
          isLoading={isLoading}
          disabled={!isValid}
        >
          {submitLabel}
        </MainButton>
      </div>
    </form>
  );
}
